import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Clock3,
  FileText,
  LockKeyhole,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";

import "./student-case-portal.css";

const steps = [
  "FIR details",
  "Student details",
  "Incident (Optional)",
  "Review & submit",
];

const blankForm = {
  fullName: "",
  fatherName: "",
  dob: "",
  gender: "",
  mobile: "",
  whatsapp: "",
  email: "",
  currentAddress: "",
  permanentAddress: "",
  college: "",
  course: "",
  year: "",
  enrollment: "",
  studentId: "",
  collegeAddress: "",

  firRegistered: "Yes",
  firNumber: "",
  firDate: "",
  policeStation: "",
  stationAddress: "",
  sections: "",
  caseType: "",
  firStatus: "Under Investigation",
  hasFirCopy: "Yes",

  incidentDate: "",
  incidentTime: "",
  incidentLocation: "",
  incidentCity: "",
  incidentDistrict: "",
  incidentState: "",
  description: "",
  roleDetails: "",
  whyFiled: "",
  present: "",
  arrested: "No",
  arrestDate: "",
  detained: "",
  policeStatements: "",
  otherIncident: "",
  currentStatus: "Named Accused",

  ioName: "",
  ioRank: "",
  ioContact: "",
  ioEmail: "",
  firstPoliceContact: "",
  lastPoliceContact: "",
  noticeReceived: "No",
  noticeDate: "",
  noticeType: "",
  statementGiven: "No",
  summonsDetails: "",
  investigationStatus: "",
  otherPolice: "",

  bailApplied: "No",
  arrestLocation: "",
  bailStatus: "",
  bailNumber: "",
  bailDate: "",
  bailGrantedDate: "",
  bailOrderNumber: "",
  bailCourt: "",
  bailConditions: "",

  courtReached: "No",
  courtName: "",
  courtLocation: "",
  courtDistrict: "",
  courtState: "",
  caseNumber: "",
  cnr: "",
  courtFilingDate: "",
  nextHearing: "",
  caseStage: "FIR Stage",
  judge: "",
  advocateName: "",
  advocateContact: "",
  otherCourt: "",

  noLawyer: false,
  lawFirm: "",
  advocateEmail: "",
  barNumber: "",
  advocateCourt: "",
  advocateRole: "",
  otherLawyer: "",

  collegeInformed: "No",
  universityInformed: "No",
  collegeAction: "No",
  suspension: "No",
  showCause: "No",
  disciplinary: "No",
  examImpact: "No",
  attendanceImpact: "No",
  scholarshipImpact: "No",
  hostelImpact: "No",
  admissionImpact: "No",
  degreeImpact: "No",
  academicOther: "",

  concern: "",
  help: "",
  urgentDate: "",
  urgentDeadline: "",
  onBail: "No",
  noticePending: "No",
  hearingPending: "No",
  academicIssue: "",
  anythingElse: "",
  urgent: "No",
  urgentReason: "",
};

const inputGroups = {
  student: [
    ["fullName", "Full name", "text", true],
    ["dob", "Date of birth", "date"],
    ["fatherName", "Father's name", "text", true],
    ["mobile", "Parent / guardian mobile", "tel", true],
    ["whatsapp", "Mobile No. / WhatsApp No.", "tel"],
    ["email", "Email address", "email", true],
    [
      "gender",
      "Gender",
      "select",
      false,
      ["Female", "Male", "Non-binary", "Prefer not to say"],
    ],
    ["currentAddress", "Current address", "textarea", true],
    ["permanentAddress", "Permanent address", "textarea"],
    ["college", "College / university name", "text", true],
    ["course", "Course / degree", "text", true],
    ["year", "Year / semester"],
    ["enrollment", "Enrollment / roll number"],
    ["studentId", "Student ID"],
    ["collegeAddress", "College address", "textarea"],
  ],
};

const phoneFields = new Set([
  "mobile",
  "whatsapp",
  "ioContact",
  "advocateContact",
]);

const isPhoneField = (name) => phoneFields.has(name);

const validatePhone = (value) =>
  value === "" || /^[6-9][0-9]{9}$/.test(value)
    ? ""
    : "Enter a valid 10-digit mobile number.";

const validateEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(value)
    ? ""
    : "Please enter a valid email address.";

function Field({
  name,
  label,
  type = "text",
  options = [],
  required = false,
  form,
  update,
  error,
}) {
  const inputProps = {
    id: name,
    name,
    value: form[name] || "",
    onChange: (event) =>
      update(
        name,
        isPhoneField(name)
          ? event.target.value.replace(/[^0-9]/g, "").slice(0, 10)
          : event.target.value,
        false,
        required
      ),
    onBlur: () => update(name, form[name] || "", true, required),
    "aria-invalid": Boolean(error),
    required,
    placeholder: `Enter ${label.toLowerCase()}`,
    inputMode: isPhoneField(name) ? "numeric" : undefined,
    maxLength: isPhoneField(name) ? 10 : undefined,
  };

  return (
    <label
      className={`portal-field ${
        type === "textarea" ? "portal-field-wide" : ""
      }`}
      htmlFor={name}
    >
      <span>
        {label}
      </span>

      {type === "textarea" ? (
        <textarea {...inputProps} rows="3" />
      ) : type === "select" ? (
        <select {...inputProps}>
          <option value="">Select one</option>

          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input {...inputProps} type={type} />
      )}

      {error && <small className="field-error">{error}</small>}
    </label>
  );
}

function Toggle({ name, label, form, update }) {
  return (
    <label className="choice-row">
      <span>{label}</span>

      <select
        value={form[name] || "No"}
        onChange={(event) => update(name, event.target.value)}
      >
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="Not Sure">Not Sure</option>
      </select>
    </label>
  );
}

function StudentCasePortal() {
  const [form, setForm] = useState(() => {
    try {
      return {
        ...blankForm,
        ...JSON.parse(
          localStorage.getItem("student-case-draft") || "{}"
        ),
      };
    } catch {
      return { ...blankForm };
    }
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [sameAddress, setSameAddress] = useState(() =>
    Boolean(form.currentAddress && form.currentAddress === form.permanentAddress)
  );
  const [people, setPeople] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [submissionError, setSubmissionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  const update = (name, value, onBlur = false, required = false) => {
    setForm((previous) => ({
      ...previous,
      [name]: value,
      ...(name === "currentAddress" && sameAddress
        ? { permanentAddress: value }
        : {}),
    }));

    const fieldError =
      required && !String(value).trim()
        ? "This field is required."
        : name === "email" && value
        ? validateEmail(value)
        : isPhoneField(name) && value
        ? validatePhone(value)
        : "";

    setErrors((previous) => {
      const next = { ...previous };

      if (onBlur) {
        next[name] = fieldError;
      } else if (!fieldError) {
        delete next[name];
      }

      return next;
    });
  };

  useEffect(() => {
    if (successInfo) return undefined;

    const timer = setTimeout(() => {
      localStorage.setItem(
        "student-case-draft",
        JSON.stringify(form)
      );

      setSavedAt(new Date());
    }, 600);

    return () => clearTimeout(timer);
  }, [form, successInfo]);

  const validate = () => {
    const next = {};
    const visibleFields = new Set(
      Array.from(
        document.querySelectorAll(".portal-main form [name]")
      ).map((field) => field.name)
    );

    Object.keys(form).forEach((name) => {
      if (!visibleFields.has(name)) return;

      const field = document.querySelector(
        `.portal-main form [name="${name}"]`
      );

      if (field?.required && !String(form[name] || "").trim()) {
        next[name] = "This field is required.";
        return;
      }

      if (name === "email" && form[name]) {
        const emailError = validateEmail(form[name]);

        if (emailError) {
          next[name] = emailError;
        }
      }

      if (isPhoneField(name) && form[name]) {
        const phoneError = validatePhone(form[name]);

        if (phoneError) {
          next[name] = phoneError;
        }
      }
    });

    setErrors(next);

    return Object.keys(next).length === 0;
  };

  const next = () => {
    const currentForm = document.querySelector(".portal-main form");
    if (!currentForm) return;

    if (!validate()) {
      const firstError = currentForm.querySelector(
        '[aria-invalid="true"]'
      );

      firstError?.focus();
      firstError?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (!currentForm.reportValidity()) {
      const firstInvalid = currentForm.querySelector(":invalid");

      firstInvalid?.focus();
      firstInvalid?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setCurrentStep((step) =>
      Math.min(step + 1, steps.length - 1)
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const previous = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const addFiles = (event, kind) => {
    const selectedFiles = [...event.target.files].map((file) => ({
      file,
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      type: kind,
      status: "Ready to upload",
    }));

    if (kind === "Evidence") {
      setEvidence((old) => [...old, ...selectedFiles]);
    } else {
      setDocuments((old) => [...old, ...selectedFiles]);
    }

    event.target.value = "";
  };

  const removeFile = (index, kind) => {
    if (kind === "Evidence") {
      setEvidence((old) =>
        old.filter((_, item) => item !== index)
      );
    } else {
      setDocuments((old) =>
        old.filter((_, item) => item !== index)
      );
    }
  };

  const addPerson = () => {
    setPeople((old) => [
      ...old,
      {
        name: "",
        role: "",
        relation: "",
        phone: "",
        address: "",
        named: "No",
        other: "",
      },
    ]);
  };

  const updatePerson = (index, name, value) => {
    setPeople((old) =>
      old.map((person, item) =>
        item === index
          ? {
              ...person,
              [name]: value,
            }
          : person
      )
    );
  };

  const submit = async (event) => {
    event.preventDefault();

    if (event.nativeEvent.submitter?.name !== "case-submit") return;

    if (!validate() || submitting) return;
    setSubmissionError("");
    setSubmitting(true);

    const payload = new FormData();

    payload.append(
      "caseData",
      JSON.stringify({
        form,
        people,
      })
    );

    documents.forEach((item) => {
      payload.append(
        "documents[]",
        item.file,
        item.name
      );
    });

    evidence.forEach((item) => {
      payload.append(
        "evidence[]",
        item.file,
        item.name
      );
    });

    try {
      /*
       * IMPORTANT:
       * Do NOT put Markdown [ ] ( ) around the URL.
       * This must be a normal URL string.
       */
      const response = await fetch(
        "https://harisharandevgan.com/case-api/student-case.php",
        {
          method: "POST",
          body: payload,
        }
      );

      const responseText = await response.text();

      let result;

      try {
        result = JSON.parse(responseText);
      } catch {
        throw new Error(
          "The server returned an invalid response. Please try again."
        );
      }

      if (
        !response.ok ||
        result.success !== true ||
        !result.caseReference
      ) {
        throw new Error(
          result.message ||
            "We could not submit your case information right now. Please try again later."
        );
      }

      setSuccessInfo({
        caseReference: result.caseReference,
        studentName: form.fullName,
      });

      setForm({ ...blankForm });
      setSameAddress(false);
      setDocuments([]);
      setEvidence([]);
      setPeople([]);
      setErrors({});
      setCurrentStep(0);
      setSavedAt(null);
      setSubmitted(true);

      localStorage.removeItem("student-case-draft");
    } catch (error) {
      setSubmissionError(
        error.message ||
          "We could not submit your case information right now. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Jharkhand Student FIR Support | Harisharan Devgan</title>

        <meta
          name="description"
          content="Students in Jharkhand facing FIRs related to protests can submit their case details and documents for legal review through Harisharan Devgan's initiative"
        />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <link
          rel="canonical"
          href="https://harisharandevgan.com/jharkhand-fir-details/"
        />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />

        <meta
          property="og:title"
          content="Jharkhand Student FIR Support | Harisharan Devgan"
        />

        <meta
          property="og:description"
          content="Students in Jharkhand facing FIRs related to protests can submit their case details and documents for legal review through Harisharan Devgan's initiative"
        />

        <meta
          property="og:url"
          content="https://harisharandevgan.com/jharkhand-fir-details/"
        />

        <meta
          property="og:site_name"
          content="Harisharan Devgan"
        />

        <meta
          property="og:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />

        <meta
          property="og:image:secure_url"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />

        <meta
          property="og:image:width"
          content="1024"
        />

        <meta
          property="og:image:height"
          content="576"
        />

        <meta
          property="og:image:alt"
          content="Jharkhand FIR details"
        />

        <meta
          property="og:image:type"
          content="image/webp"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Jharkhand Student FIR Support | Harisharan Devgan"
        />

        <meta
          name="twitter:description"
          content="Students in Jharkhand facing FIRs related to protests can submit their case details and documents for legal review through Harisharan Devgan's initiative"
        />

        <meta
          name="twitter:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />

        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon/favicon-32x32.png"
        />

        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon/favicon-16x16.png"
        />

        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicon/apple-touch-icon.png"
        />

        <link
          rel="manifest"
          href="/favicon/site.webmanifest"
        />

        <link
          rel="shortcut icon"
          href="/favicon/favicon.ico"
        />
      </Helmet>

      <div className="case-portal mt-10">
        <div className="portal-topbar">
          <Link to="/" className="portal-brand">
            <span className="brand-mark">
              <ShieldCheck size={20} />
            </span>

            <span>
              Student Legal
              <br />
              <b>Case Intake</b>
            </span>
          </Link>

          <div className="secure-note">
            <LockKeyhole size={16} />
            Private & secure
          </div>
        </div>

        <div className="portal-layout">
          <aside className="portal-sidebar">
            <div className="sidebar-intro">
              <span className="eyebrow">
                Case information portal
              </span>

              <h1>Tell us what happened.</h1>

              <p>
                A clear, confidential way to share your case
                with our review team.
              </p>
            </div>

            <nav>
              {steps.map((step, index) => (
                <button
                  type="button"
                  className={`${
                    index === currentStep ? "active" : ""
                  } ${index < currentStep ? "done" : ""}`}
                  key={step}
                  onClick={() =>
                    index < currentStep &&
                    setCurrentStep(index)
                  }
                  disabled={index > currentStep}
                >
                  <span>
                    {index < currentStep ? (
                      <Check size={14} />
                    ) : (
                      String(index + 1).padStart(2, "0")
                    )}
                  </span>

                  {step}
                </button>
              ))}
            </nav>

            <div className="sidebar-foot">
              <Clock3 size={16} />

              <span>
                Usually takes
                <br />
                <b>10–15 minutes</b>
              </span>
            </div>
          </aside>

          <main className="portal-main">
            <div className="mobile-progress">
              <span>
                Step {currentStep + 1} of {steps.length}
              </span>

              <strong>{steps[currentStep]}</strong>

              <div>
                <i
                  style={{
                    width: `${
                      ((currentStep + 1) / steps.length) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div className="portal-heading">
              <span className="eyebrow">
                Step {String(currentStep + 1).padStart(2, "0")}{" "}
                / {String(steps.length).padStart(2, "0")}
              </span>

              <h2>{steps[currentStep]}</h2>

              <p>
                {currentStep === 1
                  ? "Start with the details we can use to identify and contact you."
                  : currentStep === 0
                  ? "Share your FIR and police case details."
                  : "Share only what you know. You can return and edit your answers before submitting."}
              </p>
            </div>

            <form onSubmit={submit}>
              {submissionError && (
                <div
                  className="submission-error"
                  role="alert"
                >
                  {submissionError}
                </div>
              )}

              {currentStep === 1 && (
                <Section title="About you">
                  <div className="field-grid">
                    {inputGroups.student.map(
                      ([
                        name,
                        label,
                        type,
                        required,
                        options,
                      ]) => (
                        <React.Fragment key={name}>
                          <Field
                            name={name}
                            label={label}
                            type={type}
                            required={required}
                            options={options}
                            form={form}
                            update={update}
                            error={errors[name]}
                          />
                          {name === "currentAddress" && (
                            <label className="checkbox-row">
                              <input
                                type="checkbox"
                                checked={sameAddress}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setSameAddress(checked);
                                  if (checked) {
                                    update("permanentAddress", form.currentAddress);
                                  }
                                }}
                              />
                              <span>Permanent address is same as current address</span>
                            </label>
                          )}
                        </React.Fragment>
                      )
                    )}
                  </div>
                </Section>
              )}

              {currentStep === 0 && (
                <>
                  <Section title="Existing FIR or police case">
                    <Toggle
                      name="firRegistered"
                      label="Has an FIR been registered against you?"
                      form={form}
                      update={update}
                    />

                    {form.firRegistered === "Yes" && (
                      <div className="field-grid">
                        {[
                          [
                            "firNumber",
                            "FIR number",
                            "text",
                            true,
                          ],
                          [
                            "firDate",
                            "FIR registration date",
                            "date",
                            true,
                          ],
                          [
                            "policeStation",
                            "Police station name",
                            "text",
                            true,
                          ],
                          [
                            "stationAddress",
                            "Police station address",
                            "textarea",
                          ],
                          [
                            "sections",
                            "FIR sections / IPC / BNS / other Acts",
                            "text",
                            true,
                          ],
                          ["caseType", "Case type"],
                          [
                            "firStatus",
                            "FIR status",
                            "select",
                            false,
                            [
                              "Under Investigation",
                              "Arrested",
                              "Bail Granted",
                              "Bail Pending",
                              "Charge Sheet Filed",
                              "Trial Ongoing",
                              "Case Closed",
                              "Acquitted",
                              "Other",
                            ],
                          ],
                        ].map(
                          ([
                            name,
                            label,
                            type,
                            required,
                            options,
                          ]) => (
                            <Field
                              key={name}
                              name={name}
                              label={label}
                              type={type}
                              required={required}
                              options={options}
                              form={form}
                              update={update}
                              error={errors[name]}
                            />
                          )
                        )}
                      </div>
                    )}

                    <Toggle
                      name="hasFirCopy"
                      label="Do you have a copy of the FIR?"
                      form={form}
                      update={update}
                    />

                    <div className="upload-grid">
                      <Section
                        title="Upload case documents"
                        note="You can add multiple files. PDFs, images, videos and common office formats are accepted."
                      >
                        <UploadBox
                          title="Case documents"
                          files={documents}
                          onFiles={(event) =>
                            addFiles(event, "Case document")
                          }
                          removeFile={removeFile}
                        />
                      </Section>

                      <Section
                        title="Supporting evidence"
                        note="Add context for every piece of evidence so our team can understand its relevance."
                      >
                        <UploadBox
                          title="Photos, videos, audio and other evidence"
                          files={evidence}
                          onFiles={(event) =>
                            addFiles(event, "Evidence")
                          }
                          removeFile={removeFile}
                          evidence
                        />
                      </Section>
                    </div>
                  </Section>
                </>
              )}

              {currentStep === 2 && (
                <>
                  <Section
                    title="What happened?"
                    note="Please provide factual information chronologically and do not leave out important details."
                   >
                    <div className="field-grid">
                      {[
                        ["incidentDate", "Date of incident", "date"],
                        ["incidentTime", "Approximate time"],
                        [
                          "incidentLocation",
                          "Location of incident",
                        ],
                        ["incidentCity", "City"],
                        ["incidentDistrict", "District"],
                        ["incidentState", "State"],
                        [
                          "roleDetails",
                          "What is your role in the incident?",
                        ],
                        [
                          "whyFiled",
                          "Why do you believe the FIR was filed against you?",
                        ],
                        [
                          "present",
                          "Were you present at the location?",
                          "select",
                          false,
                          ["Yes", "No", "Not Sure"],
                        ],
                        [
                          "arrested",
                          "Were you arrested?",
                          "select",
                          false,
                          ["Yes", "No"],
                        ],
                        [
                          "arrestDate",
                          "If arrested, arrest date",
                          "date",
                        ],
                        [
                          "detained",
                          "Were you detained / questioned by police?",
                          "select",
                          false,
                          ["Yes", "No", "Not Sure"],
                        ],
                        [
                          "policeStatements",
                          "Police statements / details",
                          "textarea",
                        ],
                        [
                          "otherIncident",
                          "Any other important information",
                          "textarea",
                        ],
                      ].map(
                        ([
                          name,
                          label,
                          type,
                          required,
                          options,
                        ]) => (
                          <Field
                            key={name}
                            name={name}
                            label={label}
                            type={type}
                            required={required}
                            options={options}
                            form={form}
                            update={update}
                            error={errors[name]}
                          />
                        )
                      )}

                      <Field
                        name="description"
                        label="Please explain the complete situation in your own words"
                        type="textarea"
                        form={form}
                        update={update}
                        error={errors.description}
                      />
                    </div>
                  </Section>

                  <Section title="Your role in the case">
                    <Field
                      name="currentStatus"
                      label="What is your current status in the case?"
                      type="select"
                      options={[
                        "Named Accused",
                        "Co-Accused",
                        "Suspect",
                        "Witness",
                        "Victim/Complainant",
                        "Other",
                        "Not Sure",
                      ]}
                      form={form}
                      update={update}
                    />
                  </Section>

                  <Section
                    title="Co-accused / other persons"
                    note="Add anyone else connected to the case."
                  >
                    <div className="people-list">
                      {people.map((person, index) => (
                        <div
                          className="person-card"
                          key={index}
                        >
                          <div className="person-card-head">
                            <b>Person {index + 1}</b>

                            <button
                              type="button"
                              onClick={() =>
                                setPeople((old) =>
                                  old.filter(
                                    (_, item) =>
                                      item !== index
                                  )
                                )
                              }
                            >
                              <Trash2 size={15} />
                              Remove
                            </button>
                          </div>

                          <div className="field-grid">
                            {[
                              ["name", "Full name"],
                              [
                                "relation",
                                "Relationship with student",
                              ],
                              ["role", "Role in case"],
                              [
                                "phone",
                                "Mobile number",
                                "tel",
                              ],
                              [
                                "address",
                                "Address",
                                "textarea",
                              ],
                              [
                                "named",
                                "Named in FIR?",
                                "select",
                                false,
                                ["Yes", "No", "Not sure"],
                              ],
                              [
                                "other",
                                "Other information",
                                "textarea",
                              ],
                            ].map(
                              ([
                                name,
                                label,
                                type,
                                ,
                                options,
                              ]) => (
                                <label
                                  className={`portal-field ${
                                    type === "textarea"
                                      ? "portal-field-wide"
                                      : ""
                                  }`}
                                  key={name}
                                >
                                  <span>{label}</span>

                                  {type ===
                                  "textarea" ? (
                                    <textarea
                                      rows="2"
                                      value={person[name]}
                                      placeholder={`Enter ${label.toLowerCase()}`}
                                      onChange={(event) =>
                                        updatePerson(
                                          index,
                                          name,
                                          event.target.value
                                        )
                                      }
                                    />
                                  ) : type === "select" ? (
                                    <select
                                      value={person[name]}
                                      onChange={(event) =>
                                        updatePerson(
                                          index,
                                          name,
                                          event.target.value
                                        )
                                      }
                                    >
                                      {options.map(
                                        (option) => (
                                          <option
                                            key={option}
                                            value={option}
                                          >
                                            {option}
                                          </option>
                                        )
                                      )}
                                    </select>
                                  ) : (
                                    <input
                                      type={type || "text"}
                                      value={person[name]}
                                      placeholder={`Enter ${label.toLowerCase()}`}
                                      onChange={(event) =>
                                        updatePerson(
                                          index,
                                          name,
                                          event.target.value
                                        )
                                      }
                                    />
                                  )}
                                </label>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="add-person"
                      onClick={addPerson}
                    >
                      <Plus size={17} />
                      Add person
                    </button>
                  </Section>
                </>
              )}

              {currentStep === 3 && (
                <Review
                  form={form}
                  documents={documents}
                  evidence={evidence}
                  onEdit={setCurrentStep}
                />
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="save-button"
                  onClick={() => {
                    localStorage.setItem(
                      "student-case-draft",
                      JSON.stringify(form)
                    );
                    setSavedAt(new Date());
                  }}
                >
                  <Save size={17} />
                  Save & continue later
                </button>

                <span className="saved-label">
                  {savedAt
                    ? `Saved ${savedAt.toLocaleTimeString(
                        [],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}`
                    : "Autosave is on"}
                </span>

                <div className="action-right">
                  {currentStep > 0 && (
                    <button
                      type="button"
                      className="back-button"
                      onClick={previous}
                    >
                      <ArrowLeft size={17} />
                      Back
                    </button>
                  )}

                  {currentStep < steps.length - 1 ? (
                    <button
                      type="button"
                      className="next-button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        next();
                      }}
                    >
                      Continue
                      <ArrowRight size={17} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      name="case-submit"
                      className="next-button"
                      disabled={submitting}
                    >
                      {submitting
                        ? "Submitting..."
                        : "Submit"}

                      {!submitting && <Check size={17} />}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </main>
        </div>
      </div>

      {submitted === true && successInfo !== null && (
        <SuccessState
          info={successInfo}
          onDone={() => {
            setSubmitted(false);
            setSuccessInfo(null);
            setSubmissionError("");
          }}
        />
      )}
    </>
  );
}

function Section({ title, note, children }) {
  return (
    <section className="portal-section">
      <div className="section-title">
        <h3>{title}</h3>

        {note && <p>{note}</p>}
      </div>

      {children}
    </section>
  );
}

function UploadBox({
  title,
  files,
  onFiles,
  removeFile,
  evidence,
}) {
  const inputId = `upload-${title.replace(
    /[^a-zA-Z0-9]/g,
    "-"
  )}`;

  const openFilePicker = () => {
    document.getElementById(inputId)?.click();
  };

  return (
    <div className="upload-wrap">
      <div
        className="drop-zone"
        onClick={openFilePicker}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" ||
            event.key === " "
          ) {
            openFilePicker();
          }
        }}
      >
        <UploadCloud size={28} />

        <strong>{title}</strong>

        <span>
          Drag and drop files here, or <u>browse</u>
        </span>

        <small>Max 25 MB per file</small>

        <input
          id={inputId}
          type="file"
          multiple
          onChange={onFiles}
          onClick={(event) => event.stopPropagation()}
        />
      </div>

      {files.length > 0 && (
        <div className="file-list">
          {files.map((file, index) => (
            <div
              className="file-item"
              key={`${file.name}-${index}`}
            >
              <FileText size={18} />

              <span>
                <b>{file.name}</b>

                <small>
                  {file.type} · {file.size} ·{" "}
                  {file.status}
                </small>
              </span>

              {removeFile && (
                <button
                  type="button"
                  aria-label={`Delete ${file.name}`}
                  onClick={() =>
                    removeFile(
                      index,
                      evidence
                        ? "Evidence"
                        : "Document"
                    )
                  }
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="upload-another"
        onClick={openFilePicker}
      >
        <Plus size={15} />
        Upload another document
      </button>
    </div>
  );
}

function Review({
  form,
  documents,
  evidence,
  onEdit,
}) {
  const groups = [
    [
      "FIR details",
      [
        "firRegistered",
        "firNumber",
        "policeStation",
        "firStatus",
      ],
    ],
    [
      "Student details",
      [
        "fullName",
        "fatherName",
        "email",
        "college",
        "course",
      ],
    ],
    [
      "Incident details",
      ["incidentDate", "description"],
    ],
  ];

  return (
    <>
      <div className="review-intro">
        <CheckCircle2 size={20} />

        <span>
          Review your information carefully before
          submitting.
        </span>
      </div>

      {groups.map(([title, keys], index) => (
        <section
          className="review-section"
          key={title}
        >
          <div>
            <h3>{title}</h3>

            <button
              type="button"
              onClick={() => onEdit(index)}
            >
              Edit
            </button>
          </div>

          {keys.map(
            (key) =>
              form[key] && (
                <p key={key}>
                  <span>
                    {key.replace(
                      /([A-Z])/g,
                      " $1"
                    )}
                  </span>

                  <b>{String(form[key])}</b>
                </p>
              )
          )}
        </section>
      ))}

      <section className="review-section">
        <div>
          <h3>Uploaded files</h3>

          <button
            type="button"
            onClick={() => onEdit(0)}
          >
            Edit
          </button>
        </div>

        <p>
          <span>Case documents</span>

          <b>
            {documents.length
              ? `${documents.length} file(s)`
              : "None added"}
          </b>
        </p>

        <p>
          <span>Supporting evidence</span>

          <b>
            {evidence.length
              ? `${evidence.length} file(s)`
              : "None added"}
          </b>
        </p>
      </section>

      <div className="declaration">
        <h3>Declaration & consent</h3>

        <label>
          <input type="checkbox" required />

          I confirm that the information and documents
          submitted are true and complete to the best of
          my knowledge.
        </label>

        <label>
          <input type="checkbox" required />

          I understand that submitting this form does not
          itself constitute legal representation or legal
          advice.
        </label>
      </div>
    </>
  );
}

function SuccessState({ info, onDone }) {
  return (
    <div
      className="success-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="success-title"
    >
      <div className="success-modal">
        <button
          type="button"
          className="success-modal-close"
          onClick={onDone}
          aria-label="Close success message"
        >
          <X size={19} />
        </button>

        <div className="success-modal-icon">
          <Check size={28} />
        </div>

        <h2 id="success-title">
          Thank you for sharing
          <br />
          <em>your information.</em>
        </h2>

        <div className="success-modal-details">
          <span>
            <b>Case reference ID</b>
            <strong>
              {info?.caseReference || "—"}
            </strong>
          </span>

          <span>
            <b>Student name</b>
            <strong>
              {info?.studentName || "—"}
            </strong>
          </span>

        </div>

        <div className="success-modal-note">
          <ShieldCheck size={17} />

          Your information has been received and will be
          handled confidentially.
        </div>

        <button
          type="button"
          className="success-done"
          onClick={onDone}
        >
          Done
          <Check size={16} />
        </button>
      </div>
    </div>
  );
}

export default StudentCasePortal;